import { styled, useTheme } from "@mui/material/styles";
import * as React from "react";
import {
  Box,
  CssBaseline,
  Drawer,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  AppBar as MuiAppBar,
  Toolbar,
  Typography,
} from "@mui/material";

import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Menu as MenuIcon,
} from "@mui/icons-material";

import { Link, useLocation } from "react-router-dom";
import { Constants } from "../../constants/Constants";
import CustomHeader from "./Header";

// Import SVGs
import DashboardIcon from "../../Assests/DashboardIcon.svg";
import userImage from "../../Assests/nuralLogo.png";
import serviceLogo from "../../Assests/serviceLogo.svg";

import { useAuth } from "../../context/AuthContext";
import { buildMenuTree } from "../../utils/menuBuilder";
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Collapse from '@mui/material/Collapse';
import { formatAnyDateToDisplay } from "../../utils/dateFormatter";

// Constants
const drawerWidth = 255;

// Styled Components
const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })(
  ({ theme, open }) => ({
    flexGrow: 1,
    padding: theme.spacing(0),
    paddingRight: 0,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: `-${drawerWidth}px`,
    marginRight: 0,
    ...(open && {
      transition: theme.transitions.create(["margin", "width"], {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
    }),
    width: "100%",
    boxSizing: "border-box",
  })
);

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  background:
    "linear-gradient(89.81deg, #33499F 0%, #169AC4 86.91%, #05CFD3 100%)",
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  width: open ? `calc(100% - ${drawerWidth}px)` : "100%",
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 0),
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
}));

// Recursive Menu Item Component
const NavItem = ({ item, currentPath, level = 0 }) => {
  const [open, setOpen] = React.useState(false);
  const hasChildren = item.children && item.children.length > 0;

  // Normalized path comparison
  const normalizePath = (p) => p?.toLowerCase().replace(/\/$/, "");
  const isActive = normalizePath(currentPath) === normalizePath(item.moduleUrl) ||
    (hasChildren && item.children.some(child => normalizePath(currentPath) === normalizePath(child.moduleUrl)));

  const handleClick = (e) => {
    if (hasChildren) {
      e.preventDefault();
      setOpen(!open);
    }
  };

  const content = (
    <ListItemButton
      selected={isActive}
      onClick={hasChildren ? handleClick : undefined}
      sx={{
        pl: level * 2 + 2,
        "&.Mui-selected": {
          background: "linear-gradient(89.81deg, rgb(51 73 159) 0%, rgb(24 148 193) 86.91%, rgb(5 205 210) 100%)",
          "&:hover": { backgroundColor: "rgba(220, 228, 255, 0.3)" },
          "& .MuiTypography-root, & .MuiListItemText-primary": { color: "white !important", fontWeight: "600" },
        },
      }}
    >
      <Box sx={{ width: 24, height: 24, mr: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <img src={DashboardIcon} style={{ filter: isActive ? 'brightness(0) invert(1)' : 'none', width: '100%' }} alt="" />
      </Box>
      <ListItemText primary={item.moduleName} />
      {hasChildren && (open ? <ExpandLess /> : <ExpandMore />)}
    </ListItemButton>
  );

  return (
    <React.Fragment>
      {hasChildren ? (
        <Box sx={{ width: '100%' }}>{content}</Box>
      ) : (
        <ListItem disablePadding>
          <Link to={item.moduleUrl || "#"} style={{ textDecoration: "none", width: "100%", color: "black" }}>
            {content}
          </Link>
        </ListItem>
      )}
      {hasChildren && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {item.children.map(child => (
              <NavItem key={child._id} item={child} currentPath={currentPath} level={level + 1} />
            ))}
          </List>
        </Collapse>
      )}
    </React.Fragment>
  );
};

// Main Component
export default function Sidebar({ children }) {
  const { modules } = useAuth();
  const [open, setOpen] = React.useState(() => {
    const savedState = localStorage.getItem("sidebarOpen");
    return savedState === null ? false : JSON.parse(savedState);
  });

  const [loginTime] = React.useState(() => formatAnyDateToDisplay(new Date(), true));
  const theme = useTheme();
  const location = useLocation();
  const currentPath = location.pathname;

  const menuTree = React.useMemo(() => buildMenuTree(modules), [modules]);

  const handleDrawerOpen = () => {
    setOpen(true);
    localStorage.setItem("sidebarOpen", "true");
  };

  const handleDrawerClose = () => {
    setOpen(false);
    localStorage.setItem("sidebarOpen", "false");
  };

  const logo = localStorage.getItem("logo");

  return (
    <Grid>
      <Box sx={{ display: "flex", height: "100vh" }}>
        <CssBaseline />

        <AppBar position="fixed" open={open}>
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={handleDrawerOpen}
              edge="start"
              sx={{
                mr: 2,
                ...(open && { display: "none" }),
                "&:focus, &:focus-visible": { outline: "none" },
              }}
            >
              <MenuIcon />
            </IconButton>
            <CustomHeader />
          </Toolbar>
        </AppBar>

        <Drawer
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
              backgroundColor: "#EFF3FE",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              alignItems: "start",
              gap: "20px",
            },
          }}
          variant="persistent"
          anchor="left"
          open={open}
        >
          <Grid sx={{ width: '100%' }}>
            <DrawerHeader>
              <IconButton onClick={handleDrawerClose} sx={{ "&:focus, &:focus-visible": { outline: "none" } }}>
                {theme.direction === "ltr" ? <ChevronLeftIcon /> : <ChevronRightIcon />}
              </IconButton>
            </DrawerHeader>

            <Box sx={{ display: "flex", flexDirection: "column", textAlign: "center", mb: 2, overflow: "hidden", width: '100%' }}>
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", m: "-20px" }}>
                <img src={logo ? logo : userImage} alt="company-image" style={{ width: "300px", height: "150px", display: "block" }} />
              </Box>
              <Box sx={{ mt: "20px" }}>
                <Typography variant="h6">Nural</Typography>
              </Box>
              <Box sx={{ fontSize: "14px", fontWeight: 600, mt: 1 }}>
                <span>{Constants.lastLogin}: {loginTime}</span>
              </Box>
            </Box>

            <List sx={{ width: '100%', mt: "-10px" }}>
              {menuTree.map((item) => (
                <NavItem key={item._id} item={item} currentPath={currentPath} />
              ))}
            </List>
          </Grid>

          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: '100%', mb: 2 }}>
            <img src={serviceLogo} alt="service-logo" style={{ width: "150px", height: "80px", display: "block" }} />
          </Box>
        </Drawer>

        <Main open={open}>
          <DrawerHeader />
          {children}
        </Main>
      </Box>
    </Grid>
  );
}
